import { dayjs } from '@noor/lib';

/**
 * Pure ports of cpanel's three list-cell timer badges (`shared/ui/
 * TimeCountdown`, `TimeElapsed`, `TimeCountDebit`). The UI ticks them once a
 * second; these return the formatted value + tone for a reference instant.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

/** Traffic-light tone of the signed countdown (cpanel red / yellow / green). */
export type TimeCountdownTone = 'late' | 'due' | 'early';

/** Lateness thresholds in whole minutes: > 3 late, > -1 due, else early. */
const LATE_AFTER_MINUTES = 3;
const DUE_AFTER_MINUTES = -1;

/**
 * cpanel `TimeCountdown.computeCountdown`: the signed distance between
 * `refMs` and `endTime`. Sign is cpanel's — still BEFORE the target reads `+`
 * (time left), at/after it reads `-` (overrun). Hours are unpadded and
 * unbounded (`1:05:42`); the tone floors the minute difference.
 */
export function formatTimeCountdown(
  endTime: string,
  refMs: number,
): { time: string; tone: TimeCountdownTone } {
  const diff = refMs - dayjs(endTime).valueOf();
  const totalSeconds = Math.floor(Math.abs(diff) / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const formatted =
    hours > 0
      ? `${hours}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;

  const diffMinutes = Math.floor(diff / MS_PER_MINUTE);
  const tone: TimeCountdownTone =
    diffMinutes > LATE_AFTER_MINUTES
      ? 'late'
      : diffMinutes > DUE_AFTER_MINUTES
        ? 'due'
        : 'early';

  return { time: (diff < 0 ? '+' : '-') + formatted, tone };
}

/**
 * cpanel `TimeElapsed.formatElapsed`: plain duration since `startTime`, no
 * sign. Clamped to zero so server/client clock skew never counts negative.
 */
export function formatTimeElapsed(startTime: string, refMs: number): string {
  const diff = Math.max(0, refMs - dayjs(startTime).valueOf());
  const totalSeconds = Math.floor(diff / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Courier debt window — the courier has this long after `handed_over_at` to
 * pay a cash debt (cpanel `TimeCountDebit`, named `HOUR_MS` there: 3 hours).
 */
export const COURIER_DEBT_WINDOW_MS = 3 * 60 * 60 * 1000;

function formatHms(ms: number) {
  const totalSeconds = Math.floor(ms / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * cpanel `TimeCountDebit`: time left in the debt window, or how far past it.
 * With `closeTime` (debt repaid) it freezes on the window remainder at that
 * instant. Past a full day it switches to whole days — returned as a
 * `{ days }` result so the UI can pluralize through i18n.
 */
export function formatDebitCountdown(
  endTime: string,
  refMs: number,
  closeTime?: string | null,
): { time: string } | { daysAgo: number } {
  const endMs = dayjs(endTime).valueOf();
  if (closeTime) {
    const diff = endMs + COURIER_DEBT_WINDOW_MS - dayjs(closeTime).valueOf();
    return { time: diff > 0 ? formatHms(diff) : `-${formatHms(Math.abs(diff))}` };
  }

  const diff = endMs + COURIER_DEBT_WINDOW_MS - refMs;
  if (diff > 0) return { time: formatHms(diff) };

  const past = Math.abs(diff);
  const days = Math.floor(past / MS_PER_DAY);
  if (days >= 1) return { daysAgo: days };
  return { time: `-${formatHms(past)}` };
}
